/* rbk-currency.js — Rush display-only currency converter (SP4)
 *
 * DISPLAY ONLY. The store charges in the base currency (INR) at checkout; this script
 * only rewrites indicative price text on storefront pages. It NEVER touches checkout,
 * cart totals, forms, or any named input (see SKIP_SELECTORS below).
 *
 * Behaviour:
 *   - Reads the chosen currency from localStorage['rbk-cc'] (default = base).
 *   - Fetches rates from https://open.er-api.com/v6/latest/{BASE} (free, no auth, CORS ok),
 *     caches them in sessionStorage with a timestamp, refetches only if > 6h old.
 *   - Converts .price / .compare-at-price / .price-item / .money text (skipping the checkout
 *     /cart-total/form areas), storing the untouched original in el.dataset.rbkOrig so
 *     switching back to base restores it EXACTLY (never re-parses a converted value).
 *   - Re-applies on DOM mutations (variant change, quick-add, section render) via a debounced
 *     MutationObserver scoped to main content.
 *   - Selecting the base currency restores every original and stops.
 *   - Everything is wrapped in try/catch: any failure disables the converter silently,
 *     leaving stock base prices on the page. It must NEVER break the page.
 *
 * Config comes from window.RBK_CC (emitted by snippets/rbk-currency.liquid).
 */
(function () {
  'use strict';

  try {
    var CFG = window.RBK_CC || {};
    var BASE = String(CFG.base || 'INR').trim().toUpperCase();
    if (!BASE) return;

    var LS_KEY = 'rbk-cc';
    var SS_KEY = 'rbk-cc-rates';
    var MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6h
    var API = 'https://open.er-api.com/v6/latest/' + encodeURIComponent(BASE);

    // Price elements we convert.
    var PRICE_SELECTOR = '.price, .compare-at-price, .price-item, .money';

    // Areas whose prices are the REAL charged amounts — never convert these.
    // Anything matching (or nested inside) one of these is skipped.
    var SKIP_SELECTORS = [
      'form',
      '[name]',                 // named inputs / anything carrying a form name
      '.cart-totals',
      '.cart-total',
      '.cart__footer',
      '.cart-footer',
      '.cart-drawer__footer',
      '.cart-summary',
      '.cart-items',            // cart line items live inside the cart form, belt-and-suspenders
      '.cart-form',
      '.totals',
      '.order-summary',
      '.checkout',
      '#cart-drawer',
      'cart-drawer-component'
    ];
    var SKIP_SELECTOR = SKIP_SELECTORS.join(',');

    // ---- helpers ------------------------------------------------------------

    function getSelected() {
      var v;
      try { v = localStorage.getItem(LS_KEY); } catch (e) { v = null; }
      v = (v || BASE).trim().toUpperCase();
      return v;
    }

    function setSelected(code) {
      try { localStorage.setItem(LS_KEY, code); } catch (e) {}
    }

    function inSkippedArea(el) {
      try {
        return !!(el.closest && el.closest(SKIP_SELECTOR));
      } catch (e) {
        return true; // if we can't tell, be safe and skip
      }
    }

    // Parse a numeric base value out of a formatted money string.
    // Handles a leading/trailing symbol (₹, Rs., $ ...), Western commas AND Indian lakh
    // grouping (1,99,999), and an optional decimal part. Returns null if it can't.
    function parseAmount(text) {
      if (text == null) return null;
      var s = String(text);
      // keep only digits, separators, and minus
      var cleaned = s.replace(/[^0-9.,\-]/g, '');
      if (!cleaned || !/[0-9]/.test(cleaned)) return null;

      var neg = cleaned.indexOf('-') !== -1;
      cleaned = cleaned.replace(/-/g, '');

      var lastDot = cleaned.lastIndexOf('.');
      var lastComma = cleaned.lastIndexOf(',');
      var decSep = -1;
      // The decimal separator is whichever of . or , appears LAST and is followed by 1–2 digits.
      var dotDec = lastDot !== -1 && /^\d{1,2}$/.test(cleaned.slice(lastDot + 1));
      var commaDec = lastComma !== -1 && /^\d{1,2}$/.test(cleaned.slice(lastComma + 1));
      if (dotDec && commaDec) {
        decSep = Math.max(lastDot, lastComma);
      } else if (dotDec) {
        decSep = lastDot;
      } else if (commaDec) {
        decSep = lastComma;
      }

      var intPart, fracPart;
      if (decSep !== -1) {
        intPart = cleaned.slice(0, decSep).replace(/[.,]/g, '');
        fracPart = cleaned.slice(decSep + 1).replace(/[.,]/g, '');
      } else {
        intPart = cleaned.replace(/[.,]/g, '');
        fracPart = '';
      }
      if (!intPart) intPart = '0';
      var num = parseFloat(intPart + (fracPart ? '.' + fracPart : ''));
      if (!isFinite(num)) return null;
      return neg ? -num : num;
    }

    function formatMoney(value, currency) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency
        }).format(value);
      } catch (e) {
        // currency code not recognised by Intl -> fall back so we never render garbage
        return null;
      }
    }

    // ---- rates cache --------------------------------------------------------

    function readCache() {
      try {
        var raw = sessionStorage.getItem(SS_KEY);
        if (!raw) return null;
        var obj = JSON.parse(raw);
        if (!obj || obj.base !== BASE || !obj.rates) return null;
        if (Date.now() - (obj.t || 0) > MAX_AGE_MS) return null;
        return obj.rates;
      } catch (e) {
        return null;
      }
    }

    function writeCache(rates) {
      try {
        sessionStorage.setItem(SS_KEY, JSON.stringify({ base: BASE, t: Date.now(), rates: rates }));
      } catch (e) {}
    }

    function fetchRates() {
      var cached = readCache();
      if (cached) return Promise.resolve(cached);
      if (typeof fetch !== 'function') return Promise.resolve(null);
      return fetch(API, { mode: 'cors' })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (data) {
          if (data && (data.result === 'success' || data.rates) && data.rates) {
            writeCache(data.rates);
            return data.rates;
          }
          return null;
        })
        .catch(function () { return null; });
    }

    // ---- conversion ---------------------------------------------------------

    var currentRates = null;

    function convertEl(el, currency, rate) {
      // store the pristine original exactly once
      if (typeof el.dataset.rbkOrig === 'undefined') {
        el.dataset.rbkOrig = el.textContent;
      }
      var base = parseAmount(el.dataset.rbkOrig);
      if (base === null) return;
      var out = formatMoney(base * rate, currency);
      if (out === null) return; // unknown currency -> leave original untouched
      // only write if changed (avoids feedback loops with the observer)
      if (el.textContent !== out) el.textContent = out;
    }

    function restoreEl(el) {
      if (typeof el.dataset.rbkOrig !== 'undefined') {
        if (el.textContent !== el.dataset.rbkOrig) el.textContent = el.dataset.rbkOrig;
      }
    }

    var applying = false;

    function apply() {
      if (applying) return;
      applying = true;
      try {
        var currency = getSelected();
        var scope = document.getElementById('MainContent') || document.body;
        var nodes = scope.querySelectorAll(PRICE_SELECTOR);

        // base selected (or no/invalid rate) -> restore everything and stop
        var rate = currentRates ? currentRates[currency] : null;
        if (currency === BASE || !rate || !isFinite(rate)) {
          for (var i = 0; i < nodes.length; i++) {
            if (inSkippedArea(nodes[i])) continue;
            restoreEl(nodes[i]);
          }
          applying = false;
          return;
        }

        for (var j = 0; j < nodes.length; j++) {
          var el = nodes[j];
          if (inSkippedArea(el)) continue;
          // skip container price nodes that only wrap other .price children (avoid double text)
          convertEl(el, currency, rate);
        }
      } catch (e) {
        /* never break the page */
      }
      applying = false;
    }

    // ---- observer (debounced) ----------------------------------------------

    var debTimer = null;
    function scheduleApply() {
      if (debTimer) clearTimeout(debTimer);
      debTimer = setTimeout(apply, 120);
    }

    function startObserver() {
      try {
        var scope = document.getElementById('MainContent') || document.body;
        if (!scope || typeof MutationObserver !== 'function') return;
        var mo = new MutationObserver(function () { scheduleApply(); });
        mo.observe(scope, { childList: true, subtree: true, characterData: true });
      } catch (e) {}
    }

    // ---- widget binding -----------------------------------------------------

    function bindWidget() {
      try {
        var sel = document.getElementById('rbk-cc-select');
        if (!sel) return;
        // reflect stored choice in the picker
        var cur = getSelected();
        var has = false;
        for (var i = 0; i < sel.options.length; i++) {
          if (sel.options[i].value.toUpperCase() === cur) { has = true; break; }
        }
        sel.value = has ? cur : BASE;
        if (!has) setSelected(BASE);

        sel.addEventListener('change', function () {
          setSelected((sel.value || BASE).trim().toUpperCase());
          apply();
        });
      } catch (e) {}
    }

    // ---- boot ---------------------------------------------------------------

    // Warm the rates cache on every load (even when the selection is base) so the first
    // switch is instant. Never blocks: apply() paints base prices immediately, then rates
    // arrive and apply() re-runs. If the fetch fails, prices simply stay in the base currency.
    function init() {
      try {
        bindWidget();
        startObserver();
        apply();
        fetchRates().then(function (rates) {
          currentRates = rates;
          apply();
        });
      } catch (e) {
        /* silent */
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  } catch (e) {
    /* top-level guard: converter disabled, base prices shown */
  }
})();
